<script setup lang="ts">
const props = defineProps<{
  event: string
  metadata: Record<string, unknown>
}>()

// For *_updated events the metadata includes a `changes` key:
// { changes: { field_name: [old_val, new_val] } }
type Changes = Record<string, [unknown, unknown]>

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  return String(v)
}

const FIELD_LABELS: Record<string, string> = {
  partner_id: 'Partner',
}

// Generic key → readable label
function labelFor(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key]
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// Determine which layout to render
const isFieldChange = props.event === 'field_updated' || props.event === 'status_changed'
const isUpdatedEvent = props.event.endsWith('_updated')
const hasChanges = isUpdatedEvent && props.metadata.changes != null

const changes = hasChanges ? (props.metadata.changes as Changes) : null

// Fields to display (excluding `changes` key for updated events)
const displayFields = Object.entries(props.metadata).filter(([k]) => k !== 'changes')
</script>

<template>
  <div class="mt-2 rounded-md border text-xs">
    <!-- field_updated / status_changed: from → to chips -->
    <template v-if="isFieldChange">
      <div class="flex flex-wrap items-center gap-1.5 px-3 py-2">
        <span v-if="metadata.field" class="text-muted-foreground">{{ labelFor(String(metadata.field)) }}:</span>
        <span class="bg-destructive/10 text-destructive rounded px-1.5 py-0.5 line-through">
          {{ formatValue(metadata.from) }}
        </span>
        <span class="text-muted-foreground">→</span>
        <span class="bg-green-500/10 text-green-700 dark:text-green-400 rounded px-1.5 py-0.5">
          {{ formatValue(metadata.to) }}
        </span>
      </div>
    </template>

    <!-- *_updated events: show changed fields as from → to pairs -->
    <template v-else-if="hasChanges && changes">
      <!-- Identifying fields row if present -->
      <div v-if="displayFields.length > 0" class="border-b px-3 py-2">
        <div class="flex flex-wrap gap-x-4 gap-y-1">
          <span v-for="[key, val] in displayFields" :key="key" class="text-muted-foreground">
            <span class="font-medium">{{ labelFor(key) }}:</span> {{ formatValue(val) }}
          </span>
        </div>
      </div>
      <div class="divide-border divide-y">
        <div v-for="(vals, field) in changes" :key="field" class="flex items-center gap-2 px-3 py-1.5">
          <span class="text-muted-foreground w-24 shrink-0">{{ labelFor(String(field)) }}</span>
          <span class="bg-destructive/10 text-destructive rounded px-1.5 py-0.5 line-through">{{ formatValue(vals[0]) }}</span>
          <span class="text-muted-foreground">→</span>
          <span class="bg-green-500/10 text-green-700 dark:text-green-400 rounded px-1.5 py-0.5">{{ formatValue(vals[1]) }}</span>
        </div>
      </div>
    </template>

    <!-- *_added / *_removed and all others: generic key-value table -->
    <template v-else>
      <div class="divide-border divide-y">
        <div v-for="[key, val] in displayFields" :key="key" class="flex items-baseline gap-2 px-3 py-1.5">
          <span class="text-muted-foreground w-28 shrink-0">{{ labelFor(key) }}</span>
          <span class="text-foreground">{{ formatValue(val) }}</span>
        </div>
        <!-- Fallback if no displayFields -->
        <div v-if="displayFields.length === 0" class="text-muted-foreground px-3 py-2 italic">No details available</div>
      </div>
    </template>
  </div>
</template>
